<script setup lang="tsx">
import { computed, ref } from 'vue'
import HeaderComponent from '../shared/HeaderComponent.vue'
import IconButton from '../shared/IconButton.vue'
import UserTable from './UserTable.vue'
import { PermAdminRoles, PermAdminUsers } from '@/models/enums-generated'
import sliderStore from '@/stores/slider'
import RoleEditor from '../role/RoleEditor.vue'
import Toolbar from '../role/RoleToolbar.vue'
import { hasPermission } from '@/services/utils'
import SelfEditor from './SelfEditor.vue'
import Modal from '../shared/Modal.vue'

const showRoleInfoModal = ref(false)
const slider = sliderStore()
const roleEditorRef = ref()

const canAdd = computed(() => hasPermission(PermAdminUsers) || hasPermission(PermAdminRoles))

const handleAddClick = () => {
	const activeTabButton = document.querySelector('.nav-tabs .nav-link.active')
	if (activeTabButton?.getAttribute('data-bs-target') === '#roles') {
		roleEditorRef.value?.addRole()
	} else {
		slider.openUser()
	}
}
</script>

<template>
	<HeaderComponent :title="$t('user.label', hasPermission(PermAdminUsers) ? 2 : 1)">
		<template #right-side>
			<IconButton
				v-if="canAdd"
				id="open-add-role"
				data-test="open-add-user"
				:title="$t('misc.add')"
				class="bi bi-plus-square"
				@click="handleAddClick"
			/>
		</template>
	</HeaderComponent>

	<ul v-if="hasPermission(PermAdminUsers) || hasPermission(PermAdminRoles)" class="nav nav-tabs" role="tablist">
		<li class="nav-item" role="presentation">
			<button
				class="nav-link active"
				id="user-tab"
				data-bs-toggle="tab"
				data-bs-target="#user"
				type="button"
				role="tab"
				aria-controls="user"
				aria-selected="true"
			>
				{{ $t('user.label') }}
			</button>
		</li>
		<li v-if="hasPermission(PermAdminUsers)" class="nav-item" role="presentation">
			<button
				class="nav-link"
				id="users-tab"
				data-bs-toggle="tab"
				data-bs-target="#users"
				type="button"
				role="tab"
				aria-controls="users"
				aria-selected="false"
			>
				{{ $t('user.label', 2) }}
			</button>
		</li>
		<li v-if="hasPermission(PermAdminRoles)" class="nav-item" role="presentation">
			<button
				class="nav-link"
				id="roles-tab"
				data-bs-toggle="tab"
				data-bs-target="#roles"
				type="button"
				role="tab"
				aria-controls="roles"
				aria-selected="false"
			>
				{{ $t('role', 2) }}
				<i class="bi bi-info-circle-fill text-muted ms-2" v-one-tooltip="$t('roles.info')" @click="showRoleInfoModal = true"></i>
			</button>
		</li>
	</ul>

	<div class="tab-content pt-3">
		<div class="tab-pane active" id="user" role="tabpanel" aria-labelledby="user-tab">
			<SelfEditor></SelfEditor>
		</div>

		<div v-if="hasPermission(PermAdminUsers)" class="tab-pane" id="users" role="tabpanel" aria-labelledby="users-tab">
			<UserTable></UserTable>
		</div>

		<div v-if="hasPermission(PermAdminRoles)" class="tab-pane" id="roles" role="tabpanel" aria-labelledby="roles-tab">
			<RoleEditor ref="roleEditorRef"></RoleEditor>

			<Toolbar
				v-if="roleEditorRef?.numChanges > 0"
				:num-changes="roleEditorRef?.numChanges"
				:can-save="roleEditorRef?.canSave"
				:is-saving="roleEditorRef?.isSaving"
				@save="roleEditorRef?.save()"
				@reset="roleEditorRef?.startReset()"
			/>
		</div>
	</div>

	<Modal v-model="showRoleInfoModal" :hide-footer="true">
		<template #header>
			<h5>{{ $t('roles.modal.title') }}</h5>
		</template>

		<template #body>
			<div>
				<p>{{ $t('roles.modal.p1') }}</p>
				<p>{{ $t('roles.modal.p2') }}</p>

				<h6 class="mt-3">{{ $t('roles.modal.actionsTitle') }}</h6>
				<ul>
					<li>{{ $t('roles.modal.actions.view') }}</li>
					<li>{{ $t('roles.modal.actions.edit') }}</li>
					<li>{{ $t('roles.modal.actions.add') }}</li>
					<li>{{ $t('roles.modal.actions.delete') }}</li>
				</ul>

				<h6 class="mt-3">{{ $t('roles.modal.permissionsTitle') }}</h6>
				<ul>
					<li>{{ $t('roles.modal.own') }}</li>
					<li>{{ $t('roles.modal.all') }}</li>
				</ul>
				<p>{{ $t('roles.modal.legend') }}</p>
				<p class="mt-3">
					<strong>{{ $t('roles.modal.autoRights1') }}</strong
					>{{ $t('roles.modal.autoRights2') }}
				</p>
				<p>{{ $t('roles.modal.example') }}</p>
				<p>
					<strong>{{ $t('roles.modal.admin1') }}</strong
					>{{ $t('roles.modal.admin2') }}
				</p>
			</div>
		</template>
	</Modal>
</template>
